import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Package, Truck, BarChart3, Settings, ListChecks, RotateCcw, LifeBuoy, FileText, Tag, Plug, PieChart, Download, Users, Bell, CreditCard, ChevronsUpDown, Check, X, BoxIcon, Store as StoreIcon, UserCircle, Sliders, TrendingUp, Warehouse, Clock, PackageX, Receipt } from 'lucide-react';
type NavItem = {
  icon: BoxIcon;
  label: string;
};
type Context = {
  id: string;
  name: string;
  icon: BoxIcon;
  description: string;
  items: NavItem[];
};
const CONTEXTS: Context[] = [{
  id: 'shipments',
  name: 'Spedizioni',
  icon: Package,
  description: 'Gestione e monitoraggio',
  items: [{
    icon: LayoutDashboard,
    label: 'Panoramica'
  }, {
    icon: Package,
    label: 'Tutte le spedizioni'
  }, {
    icon: Clock,
    label: 'Ritardi'
  }, {
    icon: PackageX,
    label: 'Giacenze'
  }]
}, {
  id: 'carriers',
  name: 'Corrieri',
  icon: Truck,
  description: 'Performance e contratti',
  items: [{
    icon: BarChart3,
    label: 'Performance'
  }, {
    icon: FileText,
    label: 'Contratti'
  }, {
    icon: Receipt,
    label: 'Verifica Fatture'
  }, {
    icon: Plug,
    label: 'Integrazioni'
  }]
}, {
  id: 'tickets',
  name: 'Ticket',
  icon: LifeBuoy,
  description: 'Supporto e richieste resi',
  items: [{
    icon: ListChecks,
    label: 'Tutti i ticket'
  }, {
    icon: RotateCcw,
    label: 'Resi da approvare'
  }, {
    icon: Tag,
    label: 'Categorie'
  }]
}, {
  id: 'analytics',
  name: 'Analytics',
  icon: BarChart3,
  description: 'Report e KPI',
  items: [{
    icon: PieChart,
    label: 'Overview'
  }, {
    icon: FileText,
    label: 'Report'
  }]
}, {
  id: 'account',
  name: 'Account',
  icon: Settings,
  description: 'Impostazioni e team',
  items: [{
    icon: Settings,
    label: 'Impostazioni'
  }, {
    icon: Users,
    label: 'Team'
  }, {
    icon: Bell,
    label: 'Notifiche'
  }]
}];
export type ClientSection = 'panoramica' | 'spedizioni' | 'resi' | 'ticket';
const CLIENT_NAV: {
  icon: BoxIcon;
  label: string;
  id: ClientSection;
}[] = [{
  icon: LayoutDashboard,
  label: 'Panoramica',
  id: 'panoramica'
}, {
  icon: Package,
  label: 'Spedizioni',
  id: 'spedizioni'
}, {
  icon: LifeBuoy,
  label: 'Ticket',
  id: 'ticket'
}];
type SidebarProps = {
  viewMode: 'operator' | 'client';
  clientSection: ClientSection;
  onClientSectionChange: (s: ClientSection) => void;
  contextId?: string;
  activeItem?: string;
  onContextChange?: (id: string) => void;
  onActiveItemChange?: (label: string) => void;
};
export function Sidebar({
  viewMode,
  clientSection,
  onClientSectionChange,
  contextId: contextIdProp,
  activeItem: activeItemProp,
  onContextChange,
  onActiveItemChange
}: SidebarProps) {
  const [internalContextId, setInternalContextId] = useState('shipments');
  const [internalActiveItem, setInternalActiveItem] = useState('Panoramica');
  const contextId = contextIdProp ?? internalContextId;
  const activeItem = activeItemProp ?? internalActiveItem;
  const handleContextChange = (newId: string) => {
    const newCtx = CONTEXTS.find(c => c.id === newId)!;
    const firstItem = newCtx.items[0].label;
    if (onContextChange) onContextChange(newId);else setInternalContextId(newId);
    if (onActiveItemChange) onActiveItemChange(firstItem);else setInternalActiveItem(firstItem);
  };
  const handleItemClick = (label: string) => {
    if (onActiveItemChange) onActiveItemChange(label);else setInternalActiveItem(label);
  };
  const isClient = viewMode === 'client';
  return <aside className="hidden md:flex flex-col w-[240px] shrink-0 bg-[#0A0A0A] border-r border-[#1F1F1F] h-screen sticky top-0" data-id="element-3984">
      <div className="px-6 py-6 border-b border-[#1F1F1F]" data-id="element-3985">
        <img src="https://cdn.magicpatterns.com/uploads/ufiC3EWUw7dMTCsenDXGVj/4_6021369468377112115.svg" alt="Aisac" className="h-7 w-auto" style={{
        filter: 'brightness(0) invert(1)'
      }} data-id="element-3986" />
      </div>

      {isClient ? <>
          <div className="px-6 pt-5 pb-2" data-id="element-3987">
            <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-3988">
              Area cliente
            </div>
          </div>
          <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto" data-id="element-3989">
            <ClientNavList clientSection={clientSection} onClientSectionChange={onClientSectionChange} data-id="element-3990" />
          </nav>
        </> : <>
          <OperatorContextSwitcher contextId={contextId} onContextChange={handleContextChange} data-id="element-3991" />
          <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto" data-id="element-3992">
            <OperatorNavList contextId={contextId} activeItem={activeItem} onItemClick={handleItemClick} data-id="element-3993" />
          </nav>
        </>}

      <div className="px-6 py-4 border-t border-[#1F1F1F]" data-id="element-3994">
        <div className="flex items-center justify-between gap-2" data-id="element-3995">
          <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-3996">
            {isClient ? 'Cliente' : 'Dashboard'}
          </span>
          <span className="text-[10px] font-mono text-[#9A9A9A] px-1.5 py-0.5 rounded bg-[#181818] border border-[#262626]" data-id="element-3997">
            v2.4.0
          </span>
        </div>
      </div>
    </aside>;
}
type MobileDrawerProps = {
  open: boolean;
  onClose: () => void;
  viewMode: 'operator' | 'client';
  clientSection: ClientSection;
  onClientSectionChange: (s: ClientSection) => void;
  contextId: string;
  activeItem: string;
  onContextChange: (id: string) => void;
  onActiveItemChange: (label: string) => void;
};
export function MobileDrawer({
  open,
  onClose,
  viewMode,
  clientSection,
  onClientSectionChange,
  contextId,
  activeItem,
  onContextChange,
  onActiveItemChange
}: MobileDrawerProps) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);
  const handleContextChange = (newId: string) => {
    const newCtx = CONTEXTS.find(c => c.id === newId)!;
    onContextChange(newId);
    onActiveItemChange(newCtx.items[0].label);
  };
  const handleItemClick = (label: string) => {
    onActiveItemChange(label);
    onClose();
  };
  const handleClientChange = (s: ClientSection) => {
    onClientSectionChange(s);
    onClose();
  };
  const isClient = viewMode === 'client';
  return <AnimatePresence data-id="element-3998">
      {open && <div className="md:hidden fixed inset-0 z-50" data-id="element-3999">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" data-id="element-4000" />
          <motion.aside initial={{
        x: '-100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '-100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 280
      }} className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] bg-[#0A0A0A] border-r border-[#1F1F1F] flex flex-col" role="dialog" aria-modal="true" aria-label="Menu di navigazione" data-id="element-4001">
            <div className="px-5 py-5 border-b border-[#1F1F1F] flex items-center justify-between" data-id="element-4002">
              <img src="https://cdn.magicpatterns.com/uploads/ufiC3EWUw7dMTCsenDXGVj/4_6021369468377112115.svg" alt="Aisac" className="h-7 w-auto" style={{
            filter: 'brightness(0) invert(1)'
          }} data-id="element-4003" />
              <button onClick={onClose} aria-label="Chiudi menu" className="h-9 w-9 rounded-lg flex items-center justify-center text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition" data-id="element-4004">
                <X className="h-5 w-5" data-id="element-4005" />
              </button>
            </div>

            {isClient ? <>
                <div className="px-5 pt-4 pb-2" data-id="element-4006">
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-4007">
                    Area cliente
                  </div>
                </div>
                <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto" data-id="element-4008">
                  <ClientNavList clientSection={clientSection} onClientSectionChange={handleClientChange} data-id="element-4009" />
                </nav>
              </> : <>
                <OperatorContextSwitcher contextId={contextId} onContextChange={handleContextChange} data-id="element-4010" />
                <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto" data-id="element-4011">
                  <OperatorNavList contextId={contextId} activeItem={activeItem} onItemClick={handleItemClick} data-id="element-4012" />
                </nav>
              </>}

            <div className="px-5 py-4 border-t border-[#1F1F1F]" data-id="element-4013">
              <span className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-4014">
                {isClient ? 'Cliente' : 'Dashboard'} · v2.4.0
              </span>
            </div>
          </motion.aside>
        </div>}
    </AnimatePresence>;
}
type BottomTabBarProps = {
  clientSection: ClientSection;
  onClientSectionChange: (s: ClientSection) => void;
};
export function BottomTabBar({
  clientSection,
  onClientSectionChange
}: BottomTabBarProps) {
  return <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0A0A0A]/95 backdrop-blur border-t border-[#1F1F1F]" style={{
    paddingBottom: 'env(safe-area-inset-bottom)'
  }} aria-label="Navigazione principale" data-id="element-4015">
      <div className="flex items-stretch" data-id="element-4016">
        {CLIENT_NAV.map(({
        icon: Icon,
        label,
        id
      }) => {
        const active = id === clientSection;
        return <button key={id} onClick={() => onClientSectionChange(id)} aria-current={active ? 'page' : undefined} className={`flex-1 flex flex-col items-center justify-center gap-1 py-2.5 min-h-[56px] transition-colors ${active ? 'text-[#D4FF3A]' : 'text-[#9A9A9A]'}`} data-id="element-4017">
              <Icon className="h-[22px] w-[22px]" strokeWidth={active ? 2 : 1.75} data-id="element-4018" />
              <span className={`text-[10px] font-medium leading-none ${active ? 'text-[#D4FF3A]' : 'text-[#9A9A9A]'}`} data-id="element-4019">
                {label}
              </span>
            </button>;
      })}
      </div>
    </nav>;
}
function OperatorContextSwitcher({
  contextId,
  onContextChange
}: {
  contextId: string;
  onContextChange: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const ctx = CONTEXTS.find(c => c.id === contextId)!;
  const CtxIcon = ctx.icon;
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const handleSwitch = (newId: string) => {
    onContextChange(newId);
    setOpen(false);
  };
  return <div className="px-3 pt-4 pb-2 relative" ref={popoverRef} data-id="element-4020">
      <button onClick={() => setOpen(v => !v)} aria-haspopup="listbox" aria-expanded={open} className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-[#181818] border border-[#262626] hover:border-[#333] focus:outline-none focus:ring-2 focus:ring-[#D4FF3A]/30 transition" data-id="element-4021">
        <div className="h-7 w-7 rounded-md bg-[#D4FF3A] text-[#0A0A0A] flex items-center justify-center shrink-0" data-id="element-4022">
          <CtxIcon className="h-4 w-4" strokeWidth={2} data-id="element-4023" />
        </div>
        <div className="flex-1 min-w-0 text-left" data-id="element-4024">
          <div className="text-sm text-[#FAFAFA] font-medium truncate" data-id="element-4025">
            {ctx.name}
          </div>
        </div>
        <ChevronsUpDown className="h-3.5 w-3.5 text-[#9A9A9A] shrink-0" data-id="element-4026" />
      </button>

      {open && <div className="absolute left-3 right-3 top-[calc(100%-4px)] z-20 rounded-lg bg-[#181818] border border-[#262626] shadow-xl overflow-hidden" data-id="element-4027">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium border-b border-[#262626]" data-id="element-4028">
            Cambia contesto
          </div>
          <ul role="listbox" className="py-1" data-id="element-4029">
            {CONTEXTS.filter(c => c.id !== 'account').map(c => {
          const Icon = c.icon;
          const selected = c.id === contextId;
          return <li key={c.id} data-id="element-4030">
                  <button role="option" aria-selected={selected} onClick={() => handleSwitch(c.id)} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[#222] transition-colors" data-id="element-4031">
                    <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${selected ? 'bg-[#D4FF3A] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#262626] text-[#9A9A9A]'}`} data-id="element-4032">
                      <Icon className="h-3.5 w-3.5" strokeWidth={2} data-id="element-4033" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-4034">
                      <div className="text-sm text-[#FAFAFA] font-medium truncate" data-id="element-4035">
                        {c.name}
                      </div>
                      <div className="text-[11px] text-[#6B6B6B] truncate" data-id="element-4036">
                        {c.description}
                      </div>
                    </div>
                    {selected && <Check className="h-3.5 w-3.5 text-[#D4FF3A] shrink-0" data-id="element-4037" />}
                  </button>
                </li>;
        })}
          </ul>
        </div>}
    </div>;
}
function OperatorNavList({
  contextId,
  activeItem,
  onItemClick
}: {
  contextId: string;
  activeItem: string;
  onItemClick: (label: string) => void;
}) {
  const ctx = CONTEXTS.find(c => c.id === contextId)!;
  return <>
      {ctx.items.map(({
      icon: Icon,
      label
    }) => {
      const active = label === activeItem;
      return <button key={label} onClick={() => onItemClick(label)} className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors min-h-[44px] ${active ? 'bg-[#181818] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818]'}`} data-id="element-4038">
            {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-[#D4FF3A] rounded-r" data-id="element-4039" />}
            <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} data-id="element-4040" />
            <span className="font-medium" data-id="element-4041">{label}</span>
          </button>;
    })}
    </>;
}
function ClientNavList({
  clientSection,
  onClientSectionChange
}: {
  clientSection: ClientSection;
  onClientSectionChange: (s: ClientSection) => void;
}) {
  return <>
      {CLIENT_NAV.map(({
      icon: Icon,
      label,
      id
    }) => {
      const active = id === clientSection;
      return <button key={id} onClick={() => onClientSectionChange(id)} className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors min-h-[44px] ${active ? 'bg-[#181818] text-[#FAFAFA]' : 'text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818]'}`} data-id="element-4042">
            {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-[#D4FF3A] rounded-r" data-id="element-4043" />}
            <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.75} data-id="element-4044" />
            <span className="font-medium" data-id="element-4045">{label}</span>
          </button>;
    })}
    </>;
}